"use client";
import { Track } from "@/src/lib/tracking";

import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "next/navigation";
import { cn } from "utils";
import type { Web3LoginSupportedType } from "@features/auth/compat/sdk-types";
import { Icon, deviceDetector } from "@ospweb/sdk-ui";
import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { EmailLoginFlow, type EmailLoginFlowRef } from "./EmailLoginFlow";
import type { EmailLoginVariant } from "./types";
import { EdgenModal } from "@components/EdgenModal";
import { useIsPc } from "@hooks/useIsPc";
import { AppFullScreenLoginModal } from "@components/AppFullLoginModal";
import { setAuthSigninEntry } from "@/src/hooks/onboarding/authSigninEntry";
import { trackAuthSigninSuccess } from "@/src/hooks/onboarding/onboardingAnalytics";
import { resolveIntroAuthPlatform } from "@components/SignInIntro/resolveIntroAuthPlatform";
import { resolveOverlapSigninExtras } from "@features/etf-overlap/tracking/authSigninSuccess";

type EmailLoginDisplayMode = "modal" | "popup" | "page";

export interface EmailLoginModalProps {
  open: boolean;
  onClose: () => void;
  onSocialClick: (type: Web3LoginSupportedType) => void;
  loading?: boolean;
  className?: string;
  /** @deprecated 使用 mode="popup" */
  isPopup?: boolean;
  /** modal=弹窗/抽屉；popup=iframe 登录；page=独立登录页全屏 */
  mode?: EmailLoginDisplayMode;
  hideCloseButton?: boolean;
  /**
   * 默认关闭行为会在验证码页先返回邮箱页，避免误触丢失已填邮箱。
   * 某些强制跳转场景需要直接关闭并交由外层处理导航。
   */
  closeBehavior?: "default" | "direct";
}

/** 统一登录弹窗：PC 使用居中 Modal，移动端使用全屏 Drawer，内容均为 EmailLoginFlow */
export function EmailLoginModal({
  open,
  onClose,
  onSocialClick,
  loading = false,
  className,
  isPopup = false,
  mode,
  hideCloseButton = false,
  closeBehavior = "default",
}: EmailLoginModalProps) {
  const [isPc] = useIsPc();
  const { device } = useParams<{ device?: DeviceType }>();
  const isMobile =
    device !== undefined ? device !== "desktop" : deviceDetector.isMobile();
  const displayMode: EmailLoginDisplayMode =
    mode ?? (isPopup ? "popup" : "modal");
  const flowKey = useMemo(() => (open ? Date.now() : 0), [open]);
  const flowRef = useRef<EmailLoginFlowRef | null>(null);
  const source =
    displayMode === "page" ? "login_page" : isMobile ? "fullscreen" : "default";

  useEffect(() => {
    if (!open) {
      return;
    }
    Track.view("view_login_modal", { source });
  }, [open, source]);

  const handleClose = () => {
    if (closeBehavior === "direct") {
      onClose();
      return;
    }
    const step = flowRef.current?.getStep?.();
    if (step === "code") {
      flowRef.current?.goBackToEmail?.();
    } else {
      onClose();
    }
  };

  const flowProps = {
    onClose: handleClose,
    onSocialClick,
    socialLoading: loading,
    signInLayout:
      displayMode === "page" ? ("flat" as const) : ("legacy" as const),
    onSuccess: () => {
      // 登录成功：LoginModalProvider.handleLoginSuccess + useOnboardingEntryGate
      //
      // `page` 模式不在这里报 —— 独立登录页由 `screens/LoginPage.tsx` 的 isLoggedIn effect
      // 统一上报（它还要覆盖社交登录回跳），两处都发会把同一次成功记两遍。
      if (displayMode === "page") {
        return;
      }
      const base = {
        platform: resolveIntroAuthPlatform(),
        method: "email",
        entry: displayMode === "popup" ? "login_popup" : "login_modal",
      };
      const extras = resolveOverlapSigninExtras();
      if (extras) {
        void extras.then((extra) =>
          trackAuthSigninSuccess({ ...base, ...extra }),
        );
      } else {
        trackAuthSigninSuccess(base);
      }
    },
    onLoginClick: (method: string) => {
      if (displayMode === "page") {
        setAuthSigninEntry("signin_page");
      }
      Track.event("click_login_button", {
        login_type: method,
        source,
      });
    },
  };

  const renderLoginFlow = ({
    variant,
    flowClassName,
  }: {
    variant: EmailLoginVariant;
    flowClassName?: string;
  }) => (
    <EmailLoginFlow
      ref={flowRef}
      key={flowKey}
      variant={variant}
      className={cn("z-10", flowClassName)}
      {...flowProps}
    />
  );

  if (displayMode === "popup") {
    return renderLoginFlow({
      variant: "popup",
      flowClassName: "mt-6 flex-1 px-6",
    });
  }

  if (displayMode === "page") {
    if (!open) {
      return null;
    }

    if (isMobile) {
      return (
        <div
          className="flex h-dvh min-h-0 w-full flex-col"
          data-dismiss-kbd-scope="1"
        >
          {renderLoginFlow({ variant: "mobile" })}
        </div>
      );
    }

    return (
      <div className="flex h-dvh w-full items-center justify-center bg-background_solid_neutral_secondary px-4">
        {renderLoginFlow({
          variant: "desktop",
          flowClassName: "w-full max-w-[420px]",
        })}
      </div>
    );
  }

  return (
    <>
      {!isMobile && (
        <EdgenModal
          open={open}
          onClose={handleClose}
          centered
          title={null}
          footer={null}
          closable={false}
          maskClosable={!hideCloseButton}
          styles={{
            content: {
              padding: 0,
            },
          }}
          width={isPc ? 400 : "100%"}
          className={cn(className)}
          containerClassName="overflow-hidden md:w-[400px]"
        >
          <div className="relative max-h-[85vh] overflow-y-auto p-6">
            {renderLoginFlow({ variant: "desktop" })}
          </div>
          {!hideCloseButton && (
            <div className="absolute top-3 right-3">
              <button
                type="button"
                aria-label="Back to email"
                data-openreplay-label="login_emailloginmodal_back"
                onClick={handleClose}
                className="flex items-center justify-center"
              >
                <Icon
                  name="close"
                  size={24}
                  className="text-content_tertiary hover:text-content_secondary"
                />
              </button>
            </div>
          )}
        </EdgenModal>
      )}

      {isMobile && (
        <AppFullScreenLoginModal
          isOpen={open}
          onClose={handleClose}
          hideCloseButton={true}
          fullBleed={true}
        >
          <div className="flex h-full min-h-0 w-screen max-w-[100vw] flex-col">
            {renderLoginFlow({ variant: "mobile" })}
          </div>
        </AppFullScreenLoginModal>
      )}
    </>
  );
}
